/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useCallback, useState } from 'react';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';
import { get } from 'js/lib/user';

import { Grid, IconButton, Typography2, breakpoints } from '@coursera/cds-core';

import CoachIcon from 'bundles/ai-coach-platform/components/branding/CoachIcon';
import localStorageEx from 'bundles/common/utils/localStorageEx';
import { COACH_ANIMATION_STATES } from 'bundles/unified-home-common/constants';
import useCoachWinkingAnimation from 'bundles/unified-home-common/hooks/useCoachWinkingAnimation';
import { INFERRED_ROLE_STORAGE_KEY } from 'bundles/unified-home-common/utils/showInferredRole';

type Props = {
  text?: string;
  ctaButton?: JSX.Element;
  isDismissable?: boolean;
};

const styles = {
  coach: css`
    min-height: 80px;
    border-radius: var(--cds-border-radius-200);
    background: var(--cds-color-neutral-background-primary-weak);

    &.cds-grid-item {
      padding: var(--cds-spacing-200) var(--cds-spacing-300);
    }
  `,
  coachIcon: css`
    display: flex;
    margin-right: var(--cds-spacing-200);
  `,
  text: css`
    margin-right: var(--cds-spacing-300);

    ${breakpoints.down('xs')} {
      margin-right: 0;
    }
  `,
};

/**
 * @description This component is used to show the coach onboarding banner on the home page after a user completed onboarding
 */
const CoachOnboardingBannerModule = ({ text, ctaButton, isDismissable }: Props) => {
  const userId = get().id;

  const { coachAnimationState, handleCoachStateChange } = useCoachWinkingAnimation();
  const [isDismissed, setIsDismissed] = useState<boolean>(
    isDismissable ? localStorageEx.getItem(`${INFERRED_ROLE_STORAGE_KEY}-${userId}`, JSON.parse, false) : false
  );

  const handleOnDismiss = useCallback<() => $TSFixMe>(() => {
    localStorageEx.setItem(`${INFERRED_ROLE_STORAGE_KEY}-${userId}`, true, JSON.stringify);
    setIsDismissed(true);
  }, [userId]);

  if (isDismissed) {
    return null;
  }

  return (
    <Grid
      container
      item
      xs={12}
      css={[styles.coach]}
      onMouseEnter={() => handleCoachStateChange(COACH_ANIMATION_STATES.WINK)}
      onMouseLeave={() => handleCoachStateChange(COACH_ANIMATION_STATES.DEFAULT)}
      onFocus={() => handleCoachStateChange(COACH_ANIMATION_STATES.WINK)}
      onBlur={() => handleCoachStateChange(COACH_ANIMATION_STATES.DEFAULT)}
      alignItems="center"
    >
      <Grid item xs="auto" css={styles.coachIcon} alignItems="center">
        <CoachIcon state={coachAnimationState} />
      </Grid>
      <Grid item xs container alignItems="center">
        <Grid item xs={12} sm="auto" css={styles.text}>
          <Typography2 variant="bodyPrimary" component="h4">
            {text && <FormattedHTMLMessage message={text} />}
          </Typography2>
        </Grid>

        {ctaButton && (
          <Grid item xs="auto">
            {ctaButton}
          </Grid>
        )}
      </Grid>
      {isDismissable && (
        <Grid item xs="auto">
          <IconButton size="small" intent="close" variant="ghost" onClick={handleOnDismiss} />
        </Grid>
      )}
    </Grid>
  );
};

export default CoachOnboardingBannerModule;
